<script setup lang="ts">
import SearchPartnerBar from '@/components/features/partners/SearchPartnerBar.vue'
import { storeToRefs } from 'pinia'
import { computed, provide, ref, watch } from 'vue'
import AddCompanyBySiretButton from '@/components/common/buttons/AddCompanyBySiretButton.vue'
import NothingImage from '@/components/common/ui/NothingImage.vue'
import PartnerList from '@/components/features/partners/PartnerList.vue'
import ContactTable from '@/components/features/contact/ContactTable.vue'
import OpportunityList from '@/components/features/opportunities/OpportunityList.vue'
import { usePartnerStore } from '@/stores/usePartnerStore'
import { useOpportunityStore } from '@/stores/useOpportunityStore'
import type { Context } from '@/utils/types'
import { isSiretOrSiren } from '@/utils/textUtils'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'

const partnerStore = usePartnerStore()
const {
  hasSearched,
  loadingPartners,
  searchPartners,
  loadingContacts,
  searchContacts,
  searchValue,
} = storeToRefs(partnerStore)

const opportunityStore = useOpportunityStore()
const { searchOpportunities, loadingOpportunities } = storeToRefs(opportunityStore)

const context: Context = 'search'

provide('context', context)

const partnerIds = ref<string[]>([])

const hasCreditSafe = computed(() => {
  return searchPartners.value.some((partner) => partner.creditSafe === true)
})

function showButtonAdd() {
  return isSiretOrSiren(searchValue.value)
}

watch(
  searchPartners,
  () => {
    partnerIds.value = searchPartners.value?.map((partner) => partner.id.toString()) || []

    if (!hasCreditSafe.value) {
      partnerStore.searchContactsByPartnerIds(partnerIds.value)
      opportunityStore.searchOpportunitiesByPartnerIds(partnerIds.value)
    }
  },
  { deep: true },
)
</script>

<template>
  <div class="flex sm:flex-row flex-col justify-between items-center pb-6 gap-4 sm:gap-0">
    <Bs2Title
      title="Recherche"
      subtitle="Recherchez vos fiches clients, vos contacts, vos affaires"
    />
    <div class="flex items-center gap-4">
      <AddCompanyBySiretButton
        v-if="
          showButtonAdd() &&
          searchPartners &&
          searchPartners!.length <= 0 &&
          hasSearched &&
          !loadingPartners
        "
      />
      <SearchPartnerBar class="mr-0" />
    </div>
  </div>
  <div class="search-result">
    <div v-if="!loadingPartners && searchPartners!.length <= 0 && hasSearched">
      <nothing-image />
    </div>
    <div v-else class="flex flex-col gap-7">
      <div v-if="searchPartners.length > 0 || loadingPartners" class="flex flex-col gap-2">
        <h1 class="font-bold">Raison(s) sociale(s)</h1>
        <PartnerList v-if="searchPartners.length > 0 && !loadingPartners" :data="searchPartners" />
        <div class="skeleton opacity-30 w-full h-52" v-else></div>
      </div>
      <div v-if="!hasCreditSafe && (searchContacts || loadingContacts)" class="flex flex-col gap-2">
        <h1 class="font-bold">Contact(s)</h1>
        <ContactTable
          v-if="searchContacts && !loadingPartners && !loadingContacts"
          :allow-sorting="false"
          :allow-filtering="false"
          :allow-export="false"
          :rows="searchContacts.map(contact => {
            return {
              ...contact,
              'civility': contact.title?.name || '',
              'firstname': contact.name.split(' ', 2)[0] ||'',
              'lastname': contact.name.split(' ', 2)[1] || '',
              'isSigner': contact.isSigner || false,
              'email': contact.email || '',
              'phone': contact.phone || '',
              'mobile': contact.mobile || '',
              'function': contact._function || '',
            }
          })"
          :isLoading="loadingContacts"
          @deleted="partnerStore.searchContactsByPartnerIds(partnerIds)"
        />
        <div v-else class="skeleton opacity-30 w-full h-52"></div>
      </div>
      <div
        v-if="!hasCreditSafe && (searchOpportunities || loadingOpportunities)"
        class="flex flex-col gap-2"
      >
        <h1 class="font-bold">Affaire(s)</h1>
        <OpportunityList
          v-if="searchOpportunities && !loadingPartners && !loadingOpportunities"
          :data="searchOpportunities"
          :showAddButton="false"
        />
        <div class="skeleton opacity-30 w-full h-52" v-else></div>
      </div>
    </div>
  </div>
</template>
